/**
 * `installWebVisibility` · web-side `VisibilityController` install helper.
 *
 * Called from `<SDKProvider>` during its first render so that every
 * `viewportGated:true` hook downstream sees `registry.hasVisibility() === true`
 * before its ref callback fires (refs commit between render and effects;
 * effect order is bottom-up, so we cannot install in a parent `useEffect`
 * without losing the race).
 *
 * Idempotency contract:
 *   - `installVisibility` itself is idempotent (second call is a no-op).
 *   - This helper additionally short-circuits before constructing an
 *     `IntersectionObserver`, so React StrictMode's render double-invoke
 *     never produces a wasted observer object.
 *
 * SSR contract:
 *   - The function exits cleanly when `typeof window === 'undefined'`.
 *     Callers can invoke it unconditionally.
 */

import { getRegistrySingleton } from '../../nats/registry/subject-registry.js';
import { createBrowserVisibilityController } from './visibility-controller.js';

export interface InstallWebVisibilityOptions {
  /** Custom IntersectionObserver factory — defaults to `new IntersectionObserver(cb)` over the viewport root. */
  readonly observerFactory?: (cb: IntersectionObserverCallback) => IntersectionObserver;
}

/**
 * Global key carrying the most-recent install thunk so we can re-mount
 * visibility on a freshly-constructed registry after `teardownOldRegistry()`
 * (identity flip / HMR / cross-tab cache reset).
 *
 * Without this hook, the install path runs exactly once at module load
 * (`createWebDefaultAdapter` → `installWebVisibility`). When the auth
 * identity flip wipes the registry via `preReplaceCleanup` →
 * `teardownOldRegistry`, the next `getRegistrySingleton()` returns a fresh
 * registry with NO `VisibilityController`. `useItemObserver` short-circuits
 * to no-op refs on `!registry.hasVisibility()` → per-row wire subjects
 * never register → realtime silently dies after login/logout. Re-running
 * the install thunk on the new registry closes this hole.
 */
const VISIBILITY_INSTALLER_KEY = '__morfi_visibility_reinstall_thunk__' as const;
type GlobalWithInstaller = typeof globalThis & {
  [VISIBILITY_INSTALLER_KEY]?: () => void;
};

/**
 * Re-run the most-recent `installWebVisibility` against the (presumably
 * fresh) registry singleton. Called by the identity-flip cleanup after
 * the old registry is torn down so the next mount's `useItemObserver`
 * sees `hasVisibility() === true`. No-op when no installer has been
 * registered (RN, SSR, tests that never installed).
 */
export function reinstallWebVisibility(): void {
  const g = globalThis as GlobalWithInstaller;
  g[VISIBILITY_INSTALLER_KEY]?.();
}

/**
 * Install the IntersectionObserver-backed `VisibilityController` on the
 * registry singleton. Safe to call multiple times — only the first call
 * with a usable `window` does work. The thunk is captured on `globalThis`
 * so post-teardown re-installs can run with the same factory.
 */
export function installWebVisibility(opts: InstallWebVisibilityOptions = {}): void {
  if (typeof window === 'undefined') return;

  // If the caller hasn't supplied a factory, fall back to the global
  // `IntersectionObserver`. Some environments (notably jsdom) ship
  // neither — bail out so the registry stays in its no-controller
  // state and `viewportGated:true` hooks fail loudly when mounted.
  const factory =
    opts.observerFactory ??
    (typeof IntersectionObserver !== 'undefined'
      ? (cb: IntersectionObserverCallback): IntersectionObserver => new IntersectionObserver(cb)
      : undefined);

  if (factory === undefined) return;

  // Capture the install thunk so `reinstallWebVisibility()` can re-run it
  // after a registry teardown. Last writer wins — every call updates the
  // stored factory so the most-recent options are honoured on the next
  // reinstall.
  const installThunk = (): void => {
    const r = getRegistrySingleton();
    if (r.hasVisibility()) return;
    const v = createBrowserVisibilityController(factory);
    r.installVisibility(v);
  };
  (globalThis as GlobalWithInstaller)[VISIBILITY_INSTALLER_KEY] = installThunk;

  installThunk();
}
